import { useState, useEffect, useCallback, useRef } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import {
  X, RotateCcw, RotateCw, FlipHorizontal, FlipVertical,
  Loader2, RefreshCw, Upload, ZoomIn, ZoomOut, Move, Calendar
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { useImageEditor, EditorImage, DITHER_ALGORITHMS, DitherAlgorithm } from './useImageEditor'
import { processImageWithSettings, generatePannablePreview, PannablePreview } from './imageTransforms'
import { DISPLAY_WIDTH, DISPLAY_HEIGHT } from '../image-processing/imageProcessor'

interface ImageEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialImages: Array<{
    id: string
    file?: File
    blob?: Blob
    sourceUrl: string
    filename: string
    date: string
    previewUrl: string
  }>
  onComplete: (images: Array<{
    filename: string
    date: string
    previewUrl: string
    processedData: Uint8Array
  }>) => void
}

export function ImageEditorModal({
  open,
  onOpenChange,
  initialImages,
  onComplete,
}: ImageEditorModalProps) {
  const {
    images,
    selectedIndex,
    selectedImage,
    isProcessing,
    setSelectedIndex,
    setIsProcessing,
    addImages,
    removeImage,
    setDitherAlgorithm,
    setScale,
    setPan,
    setDate,
    rotateClockwise,
    rotateCounterClockwise,
    toggleFlipHorizontal,
    toggleFlipVertical,
    resetSettings,
    clearAll,
  } = useImageEditor()

  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [pannablePreview, setPannablePreview] = useState<PannablePreview | null>(null)

  // Pan drag state - track offset locally during drag for smooth CSS-based panning
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const dragStartRef = useRef({ x: 0, y: 0 })
  const panStartRef = useRef({ x: 0, y: 0 })

  // Initialize images when modal opens
  useEffect(() => {
    if (open && initialImages.length > 0 && images.length === 0) {
      addImages(initialImages)
    }
  }, [open, initialImages, images.length, addImages])

  // Clear images when modal closes
  useEffect(() => {
    if (!open) {
      clearAll()
      setPannablePreview(null)
    }
  }, [open, clearAll])

  // Generate pannable preview when selected image or non-pan settings change
  // Pan changes don't trigger regeneration - they're handled via CSS positioning
  const settingsWithoutPan = selectedImage ? {
    ditherAlgorithm: selectedImage.settings.ditherAlgorithm,
    rotation: selectedImage.settings.rotation,
    flipHorizontal: selectedImage.settings.flipHorizontal,
    flipVertical: selectedImage.settings.flipVertical,
    scale: selectedImage.settings.scale,
    crop: selectedImage.settings.crop,
  } : null

  useEffect(() => {
    if (!selectedImage) {
      setPannablePreview(null)
      return
    }

    let cancelled = false
    setIsGeneratingPreview(true)

    const generatePreview = async () => {
      try {
        const source = selectedImage.file || selectedImage.blob || selectedImage.sourceUrl
        const preview = await generatePannablePreview(source, selectedImage.settings)
        if (!cancelled) {
          setPannablePreview(preview)
        }
      } catch (error) {
        console.error('Failed to generate preview:', error)
      } finally {
        if (!cancelled) {
          setIsGeneratingPreview(false)
        }
      }
    }

    // Debounce preview generation
    const timeout = setTimeout(generatePreview, 150)

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage?.id, JSON.stringify(settingsWithoutPan)])

  // Handle mouse/touch drag for panning
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!selectedImage || selectedImage.settings.scale <= 1) return

    e.preventDefault()
    setIsDragging(true)
    setDragOffset({ x: 0, y: 0 })

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

    dragStartRef.current = { x: clientX, y: clientY }
    panStartRef.current = { x: selectedImage.settings.panX, y: selectedImage.settings.panY }
  }, [selectedImage])

  const handleDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !selectedImage) return

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

    // Calculate visual offset for the overlay (in screen pixels)
    const dx = clientX - dragStartRef.current.x
    const dy = clientY - dragStartRef.current.y

    setDragOffset({ x: dx, y: dy })
  }, [isDragging, selectedImage])

  const handleDragEnd = useCallback(() => {
    if (!isDragging || !selectedImage) {
      setIsDragging(false)
      return
    }

    // Convert screen offset to pan coordinates
    // dragOffset is in screen pixels, we need to convert to pan units
    // Pan is multiplied by (scale * previewScale) when positioning, so we divide to reverse
    const previewScaleValue = 0.75
    const scaleFactor = selectedImage.settings.scale * previewScaleValue
    const newPanX = panStartRef.current.x + dragOffset.x / scaleFactor
    const newPanY = panStartRef.current.y + dragOffset.y / scaleFactor

    setPan(selectedImage.id, newPanX, newPanY)
    setIsDragging(false)
    setDragOffset({ x: 0, y: 0 })
  }, [isDragging, selectedImage, dragOffset, setPan])

  // Process all images and complete
  const handleAddToQueue = useCallback(async () => {
    setIsProcessing(true)

    try {
      const results: Array<{
        filename: string
        date: string
        previewUrl: string
        processedData: Uint8Array
      }> = []

      for (const image of images) {
        const source = image.file || image.blob || image.sourceUrl
        const { bmpData, previewDataUrl } = await processImageWithSettings(source, image.settings)

        results.push({
          filename: image.filename,
          date: image.date,
          previewUrl: previewDataUrl,
          processedData: bmpData,
        })
      }

      onComplete(results)
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to process images:', error)
    } finally {
      setIsProcessing(false)
    }
  }, [images, onComplete, onOpenChange, setIsProcessing])

  if (!selectedImage) return null

  const canPan = selectedImage.settings.scale > 1
  const previewScale = 0.75

  // Calculate image position within the viewport
  // The image is centered by default, then offset by pan values
  const calculateImagePosition = () => {
    if (!pannablePreview) return { left: 0, top: 0 }

    const viewportWidth = DISPLAY_WIDTH * previewScale
    const viewportHeight = DISPLAY_HEIGHT * previewScale
    const imgWidth = pannablePreview.width * previewScale
    const imgHeight = pannablePreview.height * previewScale

    // Center position (default when pan = 0)
    const centerX = (viewportWidth - imgWidth) / 2
    const centerY = (viewportHeight - imgHeight) / 2

    // Apply pan offset (scaled to preview size) + drag offset
    const panOffsetX = selectedImage.settings.panX * (selectedImage.settings.scale) * previewScale
    const panOffsetY = selectedImage.settings.panY * (selectedImage.settings.scale) * previewScale

    return {
      left: centerX + panOffsetX + dragOffset.x,
      top: centerY + panOffsetY + dragOffset.y
    }
  }

  const imagePosition = calculateImagePosition()

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-5xl h-[90vh] bg-zinc-900 rounded-lg shadow-xl border border-zinc-800 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800 shrink-0">
            <Dialog.Title className="text-lg font-semibold text-white">
              Edit Images ({images.length})
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-zinc-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Thumbnail Grid */}
            <div className="w-48 border-r border-zinc-800 overflow-y-auto p-2 shrink-0">
              <div className="grid grid-cols-2 gap-2">
                {images.map((image, index) => (
                  <ThumbnailItem
                    key={image.id}
                    image={image}
                    isSelected={index === selectedIndex}
                    onClick={() => setSelectedIndex(index)}
                    onRemove={() => removeImage(image.id)}
                  />
                ))}
              </div>
            </div>

            {/* Center: Main Preview */}
            <div className="flex-1 flex flex-col items-center justify-center p-4 bg-zinc-950">
              <div
                className={cn(
                  'relative rounded-lg border border-zinc-700 overflow-hidden bg-zinc-800',
                  canPan && 'cursor-grab',
                  isDragging && 'cursor-grabbing'
                )}
                style={{ width: DISPLAY_WIDTH * previewScale, height: DISPLAY_HEIGHT * previewScale }}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
              >
                {isGeneratingPreview ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
                  </div>
                ) : pannablePreview ? (
                  <img
                    src={pannablePreview.dataUrl}
                    alt="Preview"
                    className="absolute select-none"
                    draggable={false}
                    style={{
                      width: pannablePreview.width * previewScale,
                      height: pannablePreview.height * previewScale,
                      maxWidth: 'none',
                      left: imagePosition.left,
                      top: imagePosition.top,
                      transition: isDragging ? 'none' : 'left 0.15s ease-out, top 0.15s ease-out'
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-zinc-500">No preview</span>
                  </div>
                )}

                {/* Pan indicator */}
                {canPan && !isDragging && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 rounded px-2 py-1 text-xs text-zinc-300">
                    <Move className="w-3 h-3" />
                    Drag to pan
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-zinc-500">
                {DISPLAY_WIDTH} x {DISPLAY_HEIGHT} pixels (e-ink display size)
              </p>
            </div>

            {/* Right: Settings Panel */}
            <div className="w-64 border-l border-zinc-800 overflow-y-auto p-4 shrink-0">
              <SettingsPanel
                image={selectedImage}
                onDitherChange={(algorithm) => setDitherAlgorithm(selectedImage.id, algorithm)}
                onScaleChange={(scale) => setScale(selectedImage.id, scale)}
                onDateChange={(date) => setDate(selectedImage.id, date)}
                onRotateClockwise={() => rotateClockwise(selectedImage.id)}
                onRotateCounterClockwise={() => rotateCounterClockwise(selectedImage.id)}
                onFlipHorizontal={() => toggleFlipHorizontal(selectedImage.id)}
                onFlipVertical={() => toggleFlipVertical(selectedImage.id)}
                onReset={() => resetSettings(selectedImage.id)}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-zinc-800 shrink-0">
            <div className="text-sm text-zinc-400">
              {images.length} image{images.length !== 1 ? 's' : ''} ready to upload
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddToQueue}
                disabled={isProcessing || images.length === 0}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Add to Queue
                  </>
                )}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

interface ThumbnailItemProps {
  image: EditorImage
  isSelected: boolean
  onClick: () => void
  onRemove: () => void
}

function ThumbnailItem({ image, isSelected, onClick, onRemove }: ThumbnailItemProps) {
  const hasTransforms = image.settings.rotation !== 0 ||
    image.settings.flipHorizontal ||
    image.settings.flipVertical ||
    image.settings.ditherAlgorithm !== 'floydSteinberg' ||
    image.settings.scale !== 1

  return (
    <div
      className={cn(
        'relative aspect-[2/3] rounded-md overflow-hidden cursor-pointer transition-all',
        'border-2',
        isSelected ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-transparent hover:border-zinc-600'
      )}
      onClick={onClick}
    >
      <img
        src={image.previewUrl}
        alt={image.filename}
        className="w-full h-full object-cover"
        style={{
          transform: `rotate(${image.settings.rotation}deg) scaleX(${image.settings.flipHorizontal ? -1 : 1}) scaleY(${image.settings.flipVertical ? -1 : 1})`,
        }}
      />

      {/* Remove button */}
      <button
        className="absolute top-1 right-1 p-0.5 bg-black/60 hover:bg-red-500 rounded transition-colors"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
      >
        <X className="w-3 h-3 text-white" />
      </button>

      {/* Transform indicator */}
      {hasTransforms && (
        <div className="absolute bottom-1 left-1 w-2 h-2 bg-yellow-500 rounded-full" title="Custom settings" />
      )}

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute inset-0 bg-blue-500/10 pointer-events-none" />
      )}
    </div>
  )
}

interface SettingsPanelProps {
  image: EditorImage
  onDitherChange: (algorithm: DitherAlgorithm) => void
  onScaleChange: (scale: number) => void
  onDateChange: (date: Date) => void
  onRotateClockwise: () => void
  onRotateCounterClockwise: () => void
  onFlipHorizontal: () => void
  onFlipVertical: () => void
  onReset: () => void
}

function SettingsPanel({
  image,
  onDitherChange,
  onScaleChange,
  onDateChange,
  onRotateClockwise,
  onRotateCounterClockwise,
  onFlipHorizontal,
  onFlipVertical,
  onReset,
}: SettingsPanelProps) {
  const currentDither = DITHER_ALGORITHMS.find(d => d.value === image.settings.ditherAlgorithm)

  // Derive the full date from the filename for the date input
  // Filename format: MM-DD_YYYY_suffix.bmp
  const getDateInputValue = () => {
    const match = image.filename.match(/^(\d{2})-(\d{2})_(\d{4})/)
    if (match) {
      return `${match[3]}-${match[1]}-${match[2]}` // YYYY-MM-DD for input[type=date]
    }
    return ''
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-zinc-300 mb-1">{image.filename}</h4>
      </div>

      {/* Date */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-white">
          <Calendar className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Date
        </h4>
        <input
          type="date"
          value={getDateInputValue()}
          onChange={(e) => {
            if (e.target.value) {
              // input[type=date] gives YYYY-MM-DD; parse as local date
              const [y, m, d] = e.target.value.split('-').map(Number)
              onDateChange(new Date(y, m - 1, d))
            }
          }}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 [color-scheme:dark]"
        />
      </div>

      {/* Dithering Algorithm */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-white">Dithering</h4>
        <select
          value={image.settings.ditherAlgorithm}
          onChange={(e) => onDitherChange(e.target.value as DitherAlgorithm)}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
        >
          {DITHER_ALGORITHMS.map((algo) => (
            <option key={algo.value} value={algo.value}>
              {algo.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-zinc-500">
          {currentDither?.description}
        </p>
      </div>

      {/* Scale/Zoom */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-white">Scale</h4>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onScaleChange(image.settings.scale - 0.25)}
            disabled={image.settings.scale <= 1}
            className="flex-shrink-0"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <input
              type="range"
              min="1"
              max="4"
              step="0.25"
              value={image.settings.scale}
              onChange={(e) => onScaleChange(parseFloat(e.target.value))}
              className="w-full accent-blue-500"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onScaleChange(image.settings.scale + 0.25)}
            disabled={image.settings.scale >= 4}
            className="flex-shrink-0"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex justify-between text-xs text-zinc-400">
          <span>Fit</span>
          <span>{Math.round(image.settings.scale * 100)}%</span>
          <span>4x</span>
        </div>
        {image.settings.scale > 1 && (
          <p className="text-xs text-blue-400">
            Drag the preview to pan
          </p>
        )}
      </div>

      {/* Rotation */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-white">Rotation</h4>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onRotateCounterClockwise}
            className="flex-1"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          <div className="flex-1 flex items-center justify-center text-sm text-zinc-300">
            {image.settings.rotation}°
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={onRotateClockwise}
            className="flex-1"
          >
            <RotateCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Flip */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-white">Flip</h4>
        <div className="flex gap-2">
          <Button
            variant={image.settings.flipHorizontal ? 'default' : 'secondary'}
            size="sm"
            onClick={onFlipHorizontal}
            className="flex-1"
          >
            <FlipHorizontal className="w-4 h-4 mr-1" />
            H
          </Button>
          <Button
            variant={image.settings.flipVertical ? 'default' : 'secondary'}
            size="sm"
            onClick={onFlipVertical}
            className="flex-1"
          >
            <FlipVertical className="w-4 h-4 mr-1" />
            V
          </Button>
        </div>
      </div>

      {/* Reset */}
      <div className="pt-4 border-t border-zinc-800">
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="w-full text-zinc-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset to Default
        </Button>
      </div>
    </div>
  )
}
